(() => {
  window.Express = window.Express || {};

  function getActiveOptionKey(context) {
    return context?.activeOptionKey || '';
  }

  function getPreviewOptionKey(context) {
    return context?.galleryPreviewOptionKey || '';
  }

  function getActiveSlideIndex(context) {
    const previewOptionKey = getPreviewOptionKey(context);
    const slides = context?.gallerySlides || [];

    if (!previewOptionKey && context?.galleryHasFallbackSlide) return 0;

    const slideIndex = slides.findIndex((slide) => slide.optionKey === previewOptionKey);
    if (slideIndex >= 0) return slideIndex;

    const fallbackIndex = slides.findIndex((slide) => slide.type === 'fallback');
    return fallbackIndex >= 0 ? fallbackIndex : 0;
  }

  function prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  function getScrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  function getScrollHelper() {
    return window.Express.productBuilderNavScroll;
  }

  function getHorizontalDragHelper() {
    return window.Express.productBuilderHorizontalDrag;
  }

  function scrollTrackToStart(track) {
    if (!track) return;
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left: 0, behavior: getScrollBehavior() });
      return;
    }

    track.scrollLeft = 0;
  }

  function sync(root, context = {}) {
    const panel = root?.querySelector?.('.epb__preview-panel.is-gallery');
    if (!panel) return;

    const activeOptionKey = getActiveOptionKey(context);
    const previewOptionKey = getPreviewOptionKey(context);
    const activeSlideIndex = getActiveSlideIndex(context);
    const slides = panel.querySelectorAll('[data-gallery-slide]');
    const thumbnails = panel.querySelectorAll('[data-gallery-thumbnail]');
    const dots = panel.querySelectorAll('[data-gallery-dot]');
    const track = panel.querySelector('.epb__gallery-track');
    const activeThumbnail = previewOptionKey
      ? panel.querySelector(`[data-gallery-thumbnail][data-option="${CSS.escape(previewOptionKey)}"]`)
      : null;

    panel.style.setProperty('--gallery-slide-index', String(activeSlideIndex));

    slides.forEach((slide, index) => {
      const isActive = index === activeSlideIndex;
      const isSelected = Boolean(activeOptionKey) && slide.dataset.option === activeOptionKey;
      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-selected', isSelected);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      const wrapper = slide.querySelector('.epb__preview-image-wrapper');
      if (wrapper) {
        wrapper.classList.toggle('is-selected', isSelected);
      }
    });

    thumbnails.forEach((thumbnail) => {
      const isSelected = Boolean(activeOptionKey) && thumbnail.dataset.option === activeOptionKey;
      const isPreviewed = Boolean(previewOptionKey) && thumbnail.dataset.option === previewOptionKey;

      thumbnail.classList.toggle('is-previewed', isPreviewed);
      thumbnail.classList.toggle('is-selected', isSelected);
      thumbnail.setAttribute('aria-current', isPreviewed ? 'true' : 'false');
      thumbnail.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      thumbnail.setAttribute('aria-label', `${isSelected ? 'Deselect' : 'Select'} ${thumbnail.dataset.optionLabel || thumbnail.dataset.option || 'option'}`);
    });

    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeSlideIndex);
    });

    if (activeThumbnail && typeof activeThumbnail.scrollIntoView === 'function') {
      activeThumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: getScrollBehavior() });
    } else if (!previewOptionKey && track) {
      scrollTrackToStart(track);
    }
  }

  function attachThumbnailDrag(track, options = {}) {
    if (!track) return null;

    let startScrollLeft = 0;
    return getHorizontalDragHelper()?.attach?.(track, {
      allowTouch: false,
      capturePointer: false,
      draggingClass: options.draggingClass || 'is-dragging',
      suppressClick: true,
      threshold: Number(options.threshold) || 6,
      onStart: () => {
        startScrollLeft = track.scrollLeft;
      },
      onMove: ({ deltaX }) => {
        track.scrollLeft = startScrollLeft - deltaX;
      },
    }) || null;
  }

  function attachPreviewDrag(stage, callbacks = {}, options = {}) {
    if (!stage) return null;
    const slides = stage.querySelector('.epb__gallery-slides');
    const navigateThreshold = Number(options.threshold) || 44;
    const dragThreshold = Number(options.dragThreshold) || Math.max(6, navigateThreshold / 2);
    const restraint = Number(options.restraint) || 1.15;

    function clearDragOffset() {
      if (!slides) return;
      slides.style.removeProperty('transition');
      slides.style.removeProperty('transform');
    }

    return getHorizontalDragHelper()?.attach?.(stage, {
      allowTouch: true,
      draggingClass: options.draggingClass || 'is-dragging',
      threshold: dragThreshold,
      onMove: ({ deltaX }) => {
        if (!slides) return;
        slides.style.transition = 'none';
        slides.style.transform = `translateX(calc((var(--gallery-slide-index, 0) * -100%) + ${deltaX}px))`;
      },
      onEnd: ({ absX, absY, direction, dragged }) => {
        clearDragOffset();
        const shouldNavigate = dragged
          && absX >= navigateThreshold
          && absX > absY * restraint;
        if (shouldNavigate && typeof callbacks.onNavigate === 'function') {
          callbacks.onNavigate(direction);
        }
      },
      onCancel: clearDragOffset,
    }) || null;
  }

  function attach(root, context = {}, callbacks = {}) {
    const panel = root?.querySelector?.('.epb__preview-panel.is-gallery');
    const track = panel?.querySelector?.('.epb__gallery-track');
    const stage = panel?.querySelector?.('.epb__gallery-stage');

    attachThumbnailDrag(track);
    attachPreviewDrag(stage, callbacks);
    getScrollHelper()?.attach?.(panel, {
      activeSelector: '.epb__gallery-option.is-previewed',
      containerSelector: '.epb__gallery',
      controlSelector: '[data-gallery-scroll-dir]',
      directionAttribute: 'galleryScrollDir',
      itemSelector: '[data-gallery-thumbnail]',
      trackSelector: '.epb__gallery-track',
    });
    sync(root, context);

    if (!panel || panel.dataset.galleryKeyboardBound === 'true') return;
    panel.dataset.galleryKeyboardBound = 'true';

    panel.addEventListener('keydown', (event) => {
      const thumbnail = event.target.closest('[data-gallery-thumbnail]');
      if (!thumbnail) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      if (typeof callbacks.onNavigate === 'function') {
        callbacks.onNavigate(direction);
      }
    });
  }

  window.Express.productBuilderGallery = {
    attach,
    sync,
  };
})();
